import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import { useSyncB2bWholesale } from '../../hooks/backend/customersPage'
import { HelpText } from '../../components/HelpText'

// Customers page > Sync to B2B: puts every wholesale customer (with an email)
// and all their orders on the B2B site. The backend works in batches, so this
// keeps calling it until no orders are left.

interface StepResult {
  customers: {
    linked: string[]
    created: string[]
    errors: { name: string; error: string }[]
  } | null
  orders: {
    created: number
    matched: number
    errors: { order: string; customer: string; error: string }[]
    remaining: number
  }
}

interface Totals {
  linked: string[]
  created: string[]
  customerErrors: { name: string; error: string }[]
  ordersCreated: number
  ordersMatched: number
  orderErrors: { order: string; customer: string; error: string }[]
  remaining: number | null
}

const EMPTY: Totals = {
  linked: [],
  created: [],
  customerErrors: [],
  ordersCreated: 0,
  ordersMatched: 0,
  orderErrors: [],
  remaining: null,
}

// Small enough to finish inside the 60 s function limit.
const BATCH = 8

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSynced: () => void
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export default function B2bSyncDialog({ open, onOpenChange, onSynced }: Props) {
  const sync = useSyncB2bWholesale()
  const triggerRef = useRef(sync.trigger)
  triggerRef.current = sync.trigger
  const stopRef = useRef(false)
  const [running, setRunning] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totals, setTotals] = useState<Totals>(EMPTY)

  useEffect(() => {
    if (!open || running) return
    setTotals(EMPTY)
    setDone(false)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const start = async () => {
    stopRef.current = false
    setRunning(true)
    setDone(false)
    setError(null)
    setTotals(EMPTY)
    const since = new Date().toISOString()
    let first = true
    try {
      for (;;) {
        const step = (await triggerRef.current({ includeCustomers: first, limit: BATCH, since })) as StepResult
        first = false
        setTotals(t => ({
          linked: [...t.linked, ...(step.customers?.linked ?? [])],
          created: [...t.created, ...(step.customers?.created ?? [])],
          customerErrors: [...t.customerErrors, ...(step.customers?.errors ?? [])],
          ordersCreated: t.ordersCreated + step.orders.created,
          ordersMatched: t.ordersMatched + step.orders.matched,
          orderErrors: [...t.orderErrors, ...step.orders.errors],
          remaining: step.orders.remaining,
        }))
        const handled = step.orders.created + step.orders.matched + step.orders.errors.length
        if (step.orders.remaining === 0 || handled === 0 || stopRef.current) break
      }
      setDone(true)
      onSynced()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync to B2B failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setRunning(false)
    }
  }

  const errorCount = totals.customerErrors.length + totals.orderErrors.length

  return (
    <Dialog open={open} onOpenChange={next => { if (!next && !running) onOpenChange(false) }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sync to B2B</DialogTitle>
          <DialogDescription><HelpText>
            Puts every wholesale customer with an email, and all their orders, on the B2B site. Customers without
            a B2B account get one (bb-70 price group). Orders that came from B2B are matched to the order already
            there; the rest are copied. Nobody gets an email and B2B stock doesn't change. New orders are also
            synced after each order import.
          </HelpText></DialogDescription>
        </DialogHeader>

        {running || done || error ? (
          <div className="flex flex-col gap-3 text-sm">
            <div className="rounded-md border p-3 space-y-1">
              <div className="flex items-center gap-2 font-medium text-foreground">
                {running ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : error ? (
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                )}
                {running ? 'Syncing…' : error ? 'Stopped' : 'Done'}
              </div>
              <div>
                Customers: {plural(totals.created.length, 'new account')}, {totals.linked.length} linked to an
                existing account
              </div>
              <div>
                Orders: {totals.ordersCreated} copied, {totals.ordersMatched} matched to a B2B order
                {totals.remaining ? ` · ${totals.remaining} left` : ''}
              </div>
              {error ? <div className="text-destructive">{error}</div> : null}
            </div>

            {totals.created.length > 0 ? (
              <div className="text-xs text-muted-foreground">New accounts: {totals.created.join(', ')}</div>
            ) : null}

            {errorCount > 0 ? (
              <div className="rounded-md border border-destructive/40 p-3 space-y-1">
                <div className="font-medium text-destructive">{plural(errorCount, 'problem')}</div>
                {totals.customerErrors.map(e => (
                  <div key={`c-${e.name}`} className="text-xs">
                    <span className="font-medium">{e.name}:</span> {e.error}
                  </div>
                ))}
                {totals.orderErrors.map(e => (
                  <div key={`o-${e.order}`} className="text-xs">
                    <span className="font-medium">{e.order} ({e.customer}):</span> {e.error}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          {running ? (
            <Button variant="outline" onClick={() => { stopRef.current = true }}>
              Stop after this batch
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => void start()}>{done || error ? 'Sync again' : 'Start sync'}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
